// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { AppIcon, Button, ButtonGroup } from '@loa/ui-components';

export type ViewMode = 'my-data' | 'all-advisors';

export type ViewToggleProps = Readonly<{
  /** Current view mode */
  currentView: ViewMode;
  /** Callback when view changes */
  onViewChange: (view: ViewMode) => void;
  /** Whether the toggle is disabled (e.g., during loading) */
  disabled?: boolean;
  /** Optional custom className for styling overrides */
  className?: string;
}>;

/**
 * ViewToggle Component
 *
 * A toggle switch for admin users to switch between personal ("My Data")
 * and brokerage-wide ("All Advisors") views.
 *
 * Uses Finerty ButtonGroup for consistent styling with the application.
 */
export function ViewToggle({
  currentView,
  onViewChange,
  disabled = false,
  className
}: ViewToggleProps) {
  const handleClick = (view: ViewMode) => {
    if (!disabled && view !== currentView) {
      onViewChange(view);
    }
  };

  const isMyDataActive = currentView === 'my-data';
  const isAllAdvisorsActive = currentView === 'all-advisors';

  return (
    <ButtonGroup className={className} role="group" aria-label="View mode toggle">
      <Button
        variant={isMyDataActive ? 'filled' : 'outlined'}
        disabled={disabled}
        onClick={() => handleClick('my-data')}
        style={{
          backgroundColor: isMyDataActive ? '#2B1B4D' : undefined,
          color: isMyDataActive ? 'white' : undefined,
        }}
        aria-pressed={isMyDataActive}
        aria-label="View my data only"
        iconLeft={<AppIcon name="user" />}
      >
        My Data
      </Button>
      <Button
        variant={isAllAdvisorsActive ? 'filled' : 'outlined'}
        disabled={disabled}
        onClick={() => handleClick('all-advisors')}
        style={{
          backgroundColor: isAllAdvisorsActive ? '#2B1B4D' : undefined,
          color: isAllAdvisorsActive ? 'white' : undefined,
        }}
        aria-pressed={isAllAdvisorsActive}
        aria-label="View all advisors data"
        iconLeft={<AppIcon name="users" />}
      >
        All Advisors
      </Button>
    </ButtonGroup>
  );
}

export default ViewToggle;
